import {
  createOneCopilotEntry as createStoredOneCopilotEntry,
  openOneCopilot,
  type OneCopilotEntryCreationResult,
  type OneCopilotEntryRequest,
  type OneCopilotEntryTarget,
} from '@github-ui/ahp-session-client/one-copilot-entry'
import {createPreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {
  beginUiReadinessHandoff,
  cancelUiReadinessHandoff,
  failUiReadinessHandoff,
  readUiReadinessHandoff,
} from '@github-ui/ahp-session-client/ui-readiness-handoff'
import type {QueryClient} from '@github-ui/react-query'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

import type {ImplicitContextPage} from '../contexts/ImplicitContextPageContext'
import {readCurrentImplicitContextPage} from '../hooks/use-current-implicit-context-page'
import type {ImplicitReference} from './implicit-context'
import {attachmentsForPage, resolveImplicitContextReferences} from './use-implicit-references'

export type OneCopilotContextIntent = 'chat' | 'task'

export interface OneCopilotContextEntryInput {
  intent: OneCopilotContextIntent
  source: string
  queryClient: QueryClient
  /**
   * Read when the handoff starts so the request carries the click-time location,
   * including a current query string or selected-line hash.
   */
  getPage?: () => ImplicitContextPage | null
}

export interface CreateOneCopilotContextEntryInput extends OneCopilotContextEntryInput {
  destination?: string
}

export interface OpenOneCopilotContextEntryInput extends CreateOneCopilotContextEntryInput {
  navigate?: (url: string) => void
  signal?: AbortSignal
}

export type OpenOneCopilotContextEntryResult = OneCopilotEntryCreationResult | {status: 'cancelled'}

function isSupportedContextReference(reference: ImplicitReference): boolean {
  switch (reference.type) {
    case 'repository':
    case 'file':
    case 'file-v2':
    case 'snippet':
    case 'file-diff':
    case 'commit':
    case 'issue':
    case 'discussion':
    case 'pull-request':
    case 'job':
      return true
    case 'release':
      return false
    default:
      return false
  }
}

function targetForPage(intent: OneCopilotContextIntent, page: ImplicitContextPage | null): OneCopilotEntryTarget {
  if (intent === 'chat') {
    return {
      kind: 'chat',
      ...(page?.owner ? {provisioningTarget: {kind: 'scope', owner: page.owner}} : {}),
    }
  }

  const nameWithOwner = page?.owner && page.repo ? `${page.owner}/${page.repo}` : undefined
  return {
    kind: 'task',
    ...(nameWithOwner ? {repository: {nameWithOwner}} : {}),
  }
}

/**
 * Builds a reviewable One Copilot request from the current GitHub page.
 *
 * Tasks carry the exact page URL as editable prompt text, which both Actions
 * and Cloud accept. Chat keeps rich attachments, falling back to the page URL
 * for releases and unsupported endpoint variants.
 */
export async function buildOneCopilotContextEntryRequest({
  intent,
  source,
  queryClient,
  getPage = readCurrentImplicitContextPage,
}: OneCopilotContextEntryInput): Promise<OneCopilotEntryRequest> {
  const seed = beginUiReadinessHandoff(source)
  const uiReadiness = seed ? {...seed, source, behavior: 'prefill' as const} : undefined
  try {
    const page = getPage()
    // Actions cannot submit AHP attachments. Keep automatic task context in the
    // visible prompt rather than restricting the user's environment choice.
    const prompt =
      intent === 'task'
        ? createPreparedPrompt({
            text: page
              ? `Context: ${new URL(page.url.replace(/^\/{2,}/, '/'), ssrSafeLocation.origin || 'https://github.com').href}`
              : '',
          })
        : createPreparedPrompt({
            text: '',
            attachments: attachmentsForPage(
              (await resolveImplicitContextReferences(queryClient, page)).filter(isSupportedContextReference),
              page,
            ),
          })

    return {
      target: targetForPage(intent, page),
      prompt,
      source,
      behavior: 'prefill',
      ...(uiReadiness ? {uiReadiness} : {}),
    }
  } catch (error) {
    if (uiReadiness) failUiReadinessHandoff(uiReadiness)
    throw error
  }
}

/** Resolves page context and stores a One Copilot entry without navigating. */
export async function createOneCopilotContextEntry({
  destination,
  ...input
}: CreateOneCopilotContextEntryInput): Promise<OneCopilotEntryCreationResult> {
  const request = await buildOneCopilotContextEntryRequest(input)
  return createStoredOneCopilotEntry(request, destination === undefined ? {} : {destination})
}

/** Resolves page context, stores the One Copilot entry, and navigates to it. */
export async function openOneCopilotContextEntry({
  destination,
  navigate,
  signal,
  ...input
}: OpenOneCopilotContextEntryInput): Promise<OpenOneCopilotContextEntryResult> {
  if (signal?.aborted) return {status: 'cancelled'}

  const request = await buildOneCopilotContextEntryRequest(input)
  if (signal?.aborted) {
    const seed = readUiReadinessHandoff(request.uiReadiness)
    if (seed) cancelUiReadinessHandoff(seed)
    return {status: 'cancelled'}
  }

  return openOneCopilot({
    ...request,
    ...(destination === undefined ? {} : {destination}),
    ...(navigate === undefined ? {} : {navigate}),
  })
}
